import { type Client } from '@microsoft/microsoft-graph-client';
import type { CloudSpendRecord } from '@/models/integrations';
import type { CloudSpend, SPListItem } from '@/models/types';
import {
  createListItem,
  getListItems,
  updateListItem,
} from './sharePointService';
import { logChange } from './snapshotService';

// ─── Azure Consumption (Cost Management API) ────────────────────────────────
/**
 * Pull Azure monthly cost breakdown for a given subscription.
 * Requires user to have Cost Management Reader role on the subscription.
 * Uses the user's Azure AD token directly (same MSAL account, re-scoped).
 */
export async function fetchAzureUsage(
  azureToken: string,
  subscriptionId: string,
  periodStart: string,
  periodEnd: string,
): Promise<CloudSpendRecord[]> {
  const url = `https://management.azure.com/subscriptions/${subscriptionId}/providers/Microsoft.CostManagement/query?api-version=2023-11-01`;
  const body = {
    type: 'ActualCost',
    timeframe: 'Custom',
    timePeriod: { from: periodStart, to: periodEnd },
    dataset: {
      granularity: 'None',
      aggregation: {
        totalCost: { name: 'Cost', function: 'Sum' },
      },
      grouping: [{ type: 'Dimension', name: 'ServiceName' }],
    },
  };

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${azureToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Azure returned ${resp.status}: ${await resp.text()}`);
  const data = await resp.json();

  const costIdx = data.properties.columns.findIndex(
    (c: { name: string }) => c.name === 'Cost',
  );
  const svcIdx = data.properties.columns.findIndex(
    (c: { name: string }) => c.name === 'ServiceName',
  );
  const currencyIdx = data.properties.columns.findIndex(
    (c: { name: string }) => c.name === 'Currency',
  );

  return (data.properties.rows ?? []).map((row: (string | number)[]) => ({
    provider: 'AzureUsage' as const,
    service: String(row[svcIdx] ?? 'Unknown'),
    subscriptionId,
    periodStart,
    periodEnd,
    amount: Number(row[costIdx] ?? 0),
    currency: currencyIdx >= 0 ? String(row[currencyIdx] ?? 'GBP') : 'GBP',
    category: 'Azure',
  }));
}

/**
 * Twilio usage — pull monthly total usage summary.
 * Uses HTTP Basic auth with AccountSid + AuthToken.
 */
export async function fetchTwilioUsage(
  accountSid: string,
  authToken: string,
  periodStart: string,
  periodEnd: string,
): Promise<CloudSpendRecord[]> {
  const url = `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Usage/Records/Monthly.json?StartDate=${periodStart}&EndDate=${periodEnd}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Basic ${btoa(`${accountSid}:${authToken}`)}` },
  });
  if (!resp.ok) throw new Error(`Twilio returned ${resp.status}`);
  const data = await resp.json();

  return (data.usage_records ?? []).map(
    (r: {
      category: string;
      price: string;
      price_unit: string;
      start_date: string;
      end_date: string;
    }) => ({
      provider: 'TwilioUsage' as const,
      service: r.category,
      subscriptionId: accountSid,
      periodStart: r.start_date,
      periodEnd: r.end_date,
      amount: parseFloat(r.price),
      currency: r.price_unit.toUpperCase(),
      category: 'Twilio',
    }),
  );
}

/**
 * GCP billing — parse BigQuery billing export CSV.
 * Expected columns: service,cost,currency,invoice_month
 */
export function parseGcpBillingCsv(csvText: string): CloudSpendRecord[] {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const serviceIdx = headers.indexOf('service');
  const costIdx = headers.findIndex((h) => h === 'cost' || h === 'total_cost');
  const currencyIdx = headers.indexOf('currency');
  const monthIdx = headers.findIndex(
    (h) => h === 'invoice_month' || h === 'month' || h === 'period',
  );
  const projectIdx = headers.indexOf('project_id');

  if (serviceIdx < 0 || costIdx < 0)
    throw new Error('CSV must have "service" and "cost" columns');

  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    const month = monthIdx >= 0 ? cols[monthIdx] : new Date().toISOString().slice(0, 7);
    return {
      provider: 'GCPUsage' as const,
      service: cols[serviceIdx] ?? 'Unknown',
      subscriptionId: projectIdx >= 0 ? cols[projectIdx] ?? 'unknown' : 'unknown',
      periodStart: month + '-01',
      periodEnd: month + '-28',
      amount: parseFloat(cols[costIdx] ?? '0'),
      currency: currencyIdx >= 0 ? cols[currencyIdx] ?? 'USD' : 'USD',
      category: 'GCP',
    };
  });
}

/**
 * Write cloud spend records to OC_CloudSpend list.
 * IDEMPOTENT: if a record already exists for (service + subscription + periodStart),
 * update its amount instead of creating a duplicate.
 */
export async function saveCloudSpend(
  client: Client,
  records: CloudSpendRecord[],
  currentUserEmail: string,
): Promise<{ saved: number; updated: number; total: number }> {
  // Fetch all existing spend records and build an index
  const existingAll = await getListItems<CloudSpend>(client, 'OC_CloudSpend');
  const keyOf = (svc: string, sub: string, start: string) =>
    `${svc}|${sub}|${start.split('T')[0]}`;
  const existingMap = new Map<string, SPListItem<CloudSpend>>();
  for (const e of existingAll) {
    existingMap.set(
      keyOf(e.fields.Service, e.fields.SubscriptionId, e.fields.PeriodStart),
      e,
    );
  }

  let saved = 0;
  let updated = 0;
  let total = 0;

  for (const r of records) {
    try {
      const key = keyOf(r.service, r.subscriptionId, r.periodStart);
      const existing = existingMap.get(key);

      const fields: Partial<CloudSpend> = {
        Title: `${r.service} - ${r.periodStart.slice(0, 7)}`,
        Provider: r.provider,
        Service: r.service,
        SubscriptionId: r.subscriptionId,
        PeriodStart: r.periodStart,
        PeriodEnd: r.periodEnd,
        Amount: r.amount,
        Currency: r.currency,
        Category: r.category,
        SyncTimestamp: new Date().toISOString(),
      };

      if (existing) {
        // Update only if amount changed meaningfully
        if (Math.abs(existing.fields.Amount - r.amount) > 0.01) {
          await updateListItem<CloudSpend>(
            client,
            'OC_CloudSpend',
            existing.id,
            fields,
          );
          updated++;
        }
      } else {
        await createListItem<CloudSpend>(client, 'OC_CloudSpend', fields);
        saved++;
      }
      total += r.amount;
    } catch (err) {
      console.error('[Prism] Failed to save spend record:', err);
    }
  }

  if (records.length > 0) {
    await logChange(
      client,
      'Synced',
      'CloudSpend',
      records[0].provider,
      records[0].provider,
      `${saved} new records, ${updated} updated, total ${total.toFixed(2)} ${records[0].currency}`,
      currentUserEmail,
    );
  }

  return { saved, updated, total };
}
